<script lang="ts" setup>
import { CATALOG_PAGE_TYPES } from '~/constants/catalogPageTypes';
import type { OfferBalance, OfferImage, OfferMedium, RecommendationsResponse } from '~/types/Catalog';

const catalogStore = useCatalogStore();
const siteStore = useSiteStore();
const { currentPageType, selectedOffer } = storeToRefs(catalogStore);
const { sendOfferViewEventToDataLayer } = useGtmModule();
const { isShowAboveHeaderBlock } = storeToRefs(siteStore);
const isShowSizesModal = ref(false);
const isShowYandexSplitModal = ref(false);

const offerIdForRecommendations = computed(() => {
  if (currentPageType.value === CATALOG_PAGE_TYPES.OFFER_GROUP_PAGE) {
    const availableOffer = selectedOffer.value.offers.find((offer) => offer.offer_balances.some((b) => b.balance > 0));
    return availableOffer?.id ?? null;
  } else {
    return selectedOffer.value.id;
  }
});

onMounted(async () => {
  const foldableBlocks = sessionStorage.getItem('productPageFoldableBlocks');

  if (foldableBlocks) {
    catalogStore.productPageFoldableBlocks = JSON.parse(foldableBlocks);
  } else {
    sessionStorage.setItem('productPageFoldableBlocks', JSON.stringify(catalogStore.productPageFoldableBlocks));
  }

  if (import.meta.client) {
    await sendOfferViewEventToDataLayer('view_item', selectedOffer.value);

    if (window.r46) {
      const { query } = useRoute();

      window.r46(
        'track',
        'view',
        query.recommended_code
          ? {
              id: offerIdForRecommendations.value,
              recommended_code: query.recommended_code,
              recommended_by: query.recommended_by,
            }
          : { id: offerIdForRecommendations.value },
      );

      for (const block of siteStore.recommendationBlocks) {
        const { type, relations, block_identifier } = block;

        if (
          selectedOfferAvailableCount.value !== null &&
          selectedOfferAvailableCount.value < 1 &&
          type === 'out_of_stock'
        ) {
          getRecommendations(block_identifier);
          continue;
        }

        const productIds = relations.products?.map((p) => p.id) || [];
        if (productIds.includes(selectedOffer.value.product_id)) {
          getRecommendations(block_identifier);
          continue;
        }

        const categories = relations.categories || [];
        for (const category of categories) {
          if (selectedOffer.value.uri.includes(category.slug)) {
            getRecommendations(block_identifier);
            break;
          }
        }
      }
    }
  }
});

const recommendationsBlock = ref<RecommendationsResponse>();
const recommendedOffers = ref<OfferMedium[]>();

function getRecommendations(blockId: string) {
  window.r46(
    'recommend',
    blockId,
    { item: offerIdForRecommendations.value },
    async function (response: RecommendationsResponse) {
      if (!response.recommends.length) return;

      recommendationsBlock.value = { ...response, block_identifier: blockId };
      recommendedOffers.value = await catalogStore.getOffersList(response.recommends.join(','));
    },
  );
}

const cartStore = useCartStore();

const countInCart = computed(() => {
  const selectedOfferInCart = cartStore.mappedCartItems.find(
    (item) => item.id === (catalogStore.selectedSizeId ?? catalogStore.selectedOffer.id),
  );

  return selectedOfferInCart?.count ?? 0;
});

const selectedOfferAvailableCount = computed(() => {
  let balance = 0;

  if (currentPageType.value === CATALOG_PAGE_TYPES.OFFER_GROUP_PAGE) {
    selectedOffer.value.offers.forEach((offer) => {
      offer.offer_balances.forEach((offerBalance: OfferBalance) => {
        if (offerBalance.warehouse.delivery_available) balance += offerBalance.balance;
      });
    });

    if (selectedOffer.value.offers.length === 0 || balance === 0) return 0;
  }

  if (!selectedOffer.value.offer_balances) return null;

  selectedOffer.value.offer_balances.forEach((offerBalance: OfferBalance) => {
    if (offerBalance.warehouse.delivery_available) balance += offerBalance.balance;
  });

  return balance;
});

const isAvailableOnlyOffline = computed(() => {
  let balanceForDelivery = 0;

  if (!selectedOffer.value.offer_balances) return null;

  selectedOffer.value.offer_balances?.forEach((offerBalance: OfferBalance) => {
    if (offerBalance.warehouse.delivery_available) balanceForDelivery += offerBalance.balance;
  });

  return (
    balanceForDelivery < 1 &&
    selectedOffer.value.offer_balances.some((offerBalance: OfferBalance) => offerBalance.balance > 0)
  );
});

const filteredProductProperties = computed(() =>
  selectedOffer.value.product_properties.filter((property) => property.value && String(property.value) !== '0'),
);

const isVideoPillActive = computed(() => selectedOffer.value.video_frames.length > 0);

const navPills = ref([
  { id: 'description', title: 'Описание', active: true },
  { id: 'characteristics', title: 'Характеристики', active: true },
  { id: 'videos', title: 'Видео', active: isVideoPillActive },
  { id: 'feedback', title: 'Отзывы и Вопросы', active: true },
]);

useJsonld(() => {
  return selectedOffer.value
    ? selectedOffer.value.offer_images.map((image) => ({
        '@context': 'https://schema.org',
        '@type': 'ImageObject',
        contentUrl: image.url,
        embeddedTextCaption: selectedOffer.value.product_name,
        creator: {
          '@type': 'Organization',
          name: 'hellride.ru',
        },
      }))
    : null;
});

// @ts-ignore
useJsonld(() => {
  return selectedOffer.value ? selectedOffer.value.schema : null;
});

const route = useRoute();
const { selectedSizeId } = storeToRefs(useCatalogStore());

const query = computed(() => {
  return selectedSizeId.value ? { ...route.query, offer_id: selectedSizeId.value } : route.query;
});

const images = ref<OfferImage[]>([...selectedOffer.value.offer_images, ...selectedOffer.value.product_images]);

useHead({
  title: selectedOffer.value.seo_data.title,
  meta: [
    { name: 'description', content: selectedOffer.value.seo_data.description },
    { property: 'og:title', content: selectedOffer.value.seo_data.title },
    { property: 'og:description', content: selectedOffer.value.seo_data.description },
    { property: 'og:type', content: 'product' },
    { property: 'og:url', content: route.fullPath },
    { property: 'og:site_name', content: 'Hellride.ru' },
    { property: 'og:locale', content: 'ru_RU' },
    { property: 'og:image', content: images.value.length ? images.value[0].url : '' },
  ],
});
</script>

<template>
  <div class="product-page">
    <CatalogBreadcrumbsBlock :breadcrumbs="selectedOffer.breadcrumbs" />

    <div class="product-page__container">
      <ProductPageImageBlock
        :alt="selectedOffer.product_name"
        class="order-1 md:order-none"
      />

      <div
        :class="{ 'with-top-bar': isShowAboveHeaderBlock }"
        class="product-page__sticky-right-side"
      >
        <section class="order-0 md:order-none product-page__main-details">
          <div class="flex items-center md:items-start gap-4">
            <div class="flex flex-col flex-grow gap-2">
              <h1 class="text-lg sm:text-2xl">{{ selectedOffer.product_name }}</h1>

              <div class="hidden md:flex gap-3">
                <ProductPageReviewsLink />

                <ProductPageQuestionsLink />
              </div>
            </div>

            <AppLink
              :to="selectedOffer.brand.uri"
              class="product-page__brand-logo"
            >
              <BrandLogoBlock :brand="selectedOffer.brand" />
            </AppLink>
          </div>
        </section>

        <ProductPageOffersBlock
          v-if="currentPageType === CATALOG_PAGE_TYPES.OFFER_PAGE"
          class="order-4 md:order-none"
        />

        <ProductPageSizedOffersBlock
          v-else-if="currentPageType === CATALOG_PAGE_TYPES.OFFER_GROUP_PAGE"
          v-model="isShowSizesModal"
          class="order-4 md:order-none"
        />

        <ProductPageSizeChartBtn class="order-4 md:order-none" />

        <section class="order-2 md:order-none product-page__price">
          <div class="flex flex-col">
            <div
              v-if="selectedOffer.old_price"
              class="line-through text-xs text-hell-black-60"
            >
              {{ Number(selectedOffer.old_price).toLocaleString('ru-RU') }}&nbsp;₽
            </div>

            <h2 class="text-3xl">{{ Number(selectedOffer.price).toLocaleString('ru-RU') }}&nbsp;₽</h2>
          </div>

          <div class="flex md:hidden gap-3">
            <ProductPageReviewsLink />

            <ProductPageQuestionsLink />
          </div>

          <ProductPageCartButton
            v-model="isShowSizesModal"
            v-model:count-in-cart="countInCart"
            :is-available-only-offline="isAvailableOnlyOffline"
            :selected-offer-available-count="selectedOfferAvailableCount"
          />

          <div
            v-if="isAvailableOnlyOffline"
            class="product-page__availability-hint"
          >
            Товар доступен только для покупки в розничном магазине
          </div>

          <div
            v-else-if="selectedOfferAvailableCount !== null && selectedOfferAvailableCount < 1"
            class="product-page__availability-hint"
          >
            Товара сейчас нет в наличии
          </div>
        </section>

        <ProductPageLoyaltyBlock class="order-3 md:order-none" />

        <YandexPaySplitBadge
          v-model:is-show-split-modal="isShowYandexSplitModal"
          :amount="selectedOffer.price"
          class="order-5 md:order-none"
        >
          <ProductPageCartButton
            v-show="countInCart === 0"
            v-model="isShowSizesModal"
            v-model:count-in-cart="countInCart"
            :is-available-only-offline="isAvailableOnlyOffline"
            :selected-offer-available-count="selectedOfferAvailableCount"
            class="my-2"
            is-in-modal
            @added-to-cart="isShowYandexSplitModal = false"
          />
        </YandexPaySplitBadge>

        <ProductPageAvailabilityBlock
          v-if="selectedOffer.offer_balances"
          class="order-6 md:order-none"
        />

        <AdminOfferAvailabilityBlock
          :offer="selectedOffer"
          class="order-7"
        />

        <ProductPageMarketingTilesBlock class="order-8 md:order-none" />
      </div>

      <section
        :class="{ 'with-top-bar': isShowAboveHeaderBlock }"
        class="order-9 md:order-none product-page__nav-pills"
      >
        <template
          v-for="pill in navPills"
          :key="pill.id"
        >
          <NuxtLink
            v-if="pill.active"
            :to="{ query, hash: `#${pill.id}` }"
            class="product-page__nav-pills-item"
          >
            {{ pill.title }}
          </NuxtLink>
        </template>
      </section>

      <section class="order-10 md:order-none product-page__foldable-blocks">
        <ProductPageFeedFoldableBlock
          id="description"
          title="Описание"
        >
          <div
            v-if="selectedOffer.description"
            class="product-page__description"
            v-html="selectedOffer.description"
          />
          <p v-else>Описания пока нет, но скоро будет ;)</p>
        </ProductPageFeedFoldableBlock>

        <ProductPageFeedFoldableBlock
          v-if="filteredProductProperties.length"
          id="characteristics"
          title="Характеристики"
        >
          <ProductPageCharacteristicsBlock
            :product-properties="filteredProductProperties"
            :product-properties-groups="selectedOffer.product_property_groups"
            class="pt-2"
          />
        </ProductPageFeedFoldableBlock>

        <ProductPageFeedFoldableBlock
          v-if="selectedOffer.video_frames.length"
          id="videos"
          title="Видео"
        >
          <div
            v-for="(video, key) in selectedOffer.video_frames"
            :key
            class="flex flex-col gap-4 pt-4"
            v-html="video"
          />
        </ProductPageFeedFoldableBlock>

        <ProductCardsGallery
          v-if="recommendedOffers?.length"
          :offers="recommendedOffers"
          :recommendations-block-id="recommendationsBlock?.block_identifier"
          :title="`<h2>${recommendationsBlock?.title}</h2>`"
          no-padding
        />

        <ProductPageFeedbackBlock id="feedback" />
      </section>
    </div>
  </div>
</template>

<style lang="scss">
.product-page {
  gap: 16px;

  @include inner-content;

  &__container {
    display: grid;
    grid-template-columns: minmax(100px, 1fr);
    row-gap: 16px;
    column-gap: 64px;

    @include screen-md {
      grid-template-columns: minmax(100px, 3fr) minmax(100px, 2fr);
    }

    @include screen-lg {
      grid-template-columns: minmax(100px, 1fr) 400px;
    }

    @include screen-xl {
      grid-template-columns: minmax(100px, 1fr) 520px;
    }
  }

  &__sticky-right-side {
    display: contents;
    border-radius: 12px;
    background-color: $hell-black-5;

    @include screen-md {
      display: flex;
      flex-direction: column;
      gap: 32px;
    }

    @include screen-lg {
      position: sticky;
      top: 128px;
      z-index: 5;
      height: fit-content;

      &.with-top-bar {
        top: 176px;
      }
    }
  }

  &__main-details {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
  }

  &__brand-logo {
    @apply flex-shrink-0 rounded-xl shadow-lg transition duration-300;
    @apply hover:shadow-sm hover:translate-y-0.5 hover:-rotate-3;

    &-image {
      object-fit: contain;
      padding: 8px;
      border-radius: 12px;
      background-color: $white;
      cursor: pointer;
    }
  }

  &__price {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    justify-content: space-between;
    align-items: center;

    @include screen-lg {
      display: grid;
      grid-template-columns: auto 1fr;
    }
  }

  &__availability-hint {
    display: none;

    @include screen-md {
      display: block;
      grid-column: span 2 / span 2;
      padding: 6px 4px;
      border: 1px solid $hell-black-20;
      border-radius: 8px;
      color: $hell-black-60;
      font-size: $text-xs;
      text-align: center;
    }
  }

  &__nav-pills {
    position: sticky;
    top: 0;
    z-index: 2;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    height: fit-content;
    margin: 0 -12px;
    padding: 12px;
    background-color: rgba($hell-black-5, 0.8);
    backdrop-filter: blur(3px);

    @include screen-md {
      grid-column: span 2;
    }

    @include screen-lg {
      top: 110px;
      grid-column: 1/2;

      &.with-top-bar {
        top: 158px;
      }
    }

    &-item {
      display: flex;
      justify-content: center;
      align-items: center;
      width: fit-content;
      height: 32px;
      padding: 0.5rem 1rem;
      border: 1px solid $hell-black-40;
      border-radius: 12px;
      font-weight: $bold;
      font-size: $text-sm;
      transition: all 0.3s;

      &:hover {
        border-color: $hell-black;
        color: $hell-black;
      }

      @include screen-sm {
        height: 40px;
      }
    }
  }

  &__description {
    padding-top: 16px;

    ul,
    ol {
      margin: 0.5rem 0;
      padding-left: 1rem;
      list-style: square;
      line-height: 1.2;
    }
  }

  &__foldable-blocks {
    display: flex;
    flex-direction: column;
    gap: 48px;
    align-items: flex-start;
    margin: 0 -12px;
    padding: 12px;
    background-color: rgba($hell-black-5, 0.8);
    font-size: $text-sm;
    backdrop-filter: blur(3px);

    @include screen-md {
      grid-column: span 2;
      font-size: unset;
    }

    @include screen-lg {
      grid-column: 1/2;
    }

    & iframe {
      width: 100%;
      height: 100%;
      aspect-ratio: 16/9;
    }
  }
}

#description,
#characteristics,
#videos,
#feedback {
  scroll-margin: 100px;

  @include screen-lg {
    scroll-margin: 220px;
  }
}
</style>
